import { Component, type ErrorInfo, type ReactNode } from 'react';

/*
 * The last thing between a thrown error and a white page.
 *
 * ⚠️ WITHOUT THIS, ANY THROW DURING RENDER UNMOUNTS THE ENTIRE TREE AND LEAVES NOTHING.
 * React 18 does that by design, and this app had no boundary at all — so a customer part-way
 * through booking saw a blank page with no message, no error, and no way back. She would
 * assume the business was broken and close the tab, and the operator would never know a
 * booking had been lost. Blank is the single worst failure mode a booking page has, because
 * it is indistinguishable from a dead site and it reports nothing to anybody.
 *
 * ⚠️ IT DOES NOT PRETEND TO RECOVER. It cannot know what went wrong or whether the booking
 * was written, so it says exactly that and offers the two things that are always true: start
 * again, or phone the business. Telling somebody "try again" about a form that may already
 * have taken her appointment would be a guess dressed up as advice.
 *
 * ⚠️ IT WEARS NO BRANDING. Everywhere else this app is careful to look like the tenant and
 * never like StartMeUpPro — but the colours come from a fetch that may be the thing that
 * failed. Neutral is the only honest choice here, and it is one screen.
 */

interface Props {
  children: ReactNode;
  /** Named so the console line says WHICH surface died — `/book`, the landing page, a step. */
  surface: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /*
     * ⚠️ CONSOLE, DELIBERATELY, AND NOTHING ELSE. There is no error-reporting service wired
     * up (and adding one silently would ship a third party into a page customers use). This
     * at least means the failure is one keystroke away for anyone asked "what does the
     * console say?" — which is exactly the question a blank page cannot answer today.
     */
    console.error(`[booking:${this.props.surface}] render failed`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center bg-white px-6 text-slate-900">
        <div className="max-w-md">
          <h1 className="text-xl font-semibold">Something went wrong on this page</h1>
          <p className="mt-2 text-slate-600">
            Sorry — the booking form stopped working. Nothing here can tell you whether your
            appointment was saved, so please phone the business to check rather than assume
            either way.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-5 rounded-xl bg-slate-900 px-5 py-3 font-semibold text-white"
          >
            Start again
          </button>
          {/*
            The message, on the page. A customer will not open a console, but she may well
            read this out over the phone or screenshot it — and that is the difference
            between a bug we can find and one we can only speculate about.
          */}
          <details className="mt-6 text-sm text-slate-500">
            <summary className="cursor-pointer">Technical details</summary>
            {/*
              ⚠️ THE STACK, NOT JUST THE MESSAGE. `n is not a function` names nothing and cost
              four rounds of guessing; the first few frames name the function. Read together
              with the sourcemaps the build now emits, a screenshot of this box is enough to
              find the bug without reproducing it — which matters, because the failures that
              reach a customer are the ones nobody here can reproduce.
            */}
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-100 p-3 text-xs">
              {this.props.surface}: {error.message}
              {'\n\n'}
              {(error.stack ?? '(no stack)').split('\n').slice(0, 8).join('\n')}
              {'\n\n'}
              {navigator.userAgent}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
